import { Component, inject, ViewChild } from '@angular/core';
import { BaseViewComponent } from '../base-view.component';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { StateChangedModel } from '../../common/services/state.service';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { Button } from 'primeng/button';
import { AddressSuggestions, SecureDataService } from '../../common/services/securedata.sevice';
import { take } from 'rxjs';
import { EmailVerificationComponent, EmailVerifyStatus } from '../../common/modules/email-verification/email-verification.component';
import { PhoneVerifyStatus } from '../../common/modules/phone-verification/phone-verification.component';
import { SignalREventService } from '../../common/services/signalR.service';
import { EMAIL_PATTERN } from '../../common/constants/validation-patterns';

@Component({
    selector: 'app-customer-information-collection',
    templateUrl: './customer-information-collection.component.html',
    styleUrl: './customer-information-collection.component.scss',
    standalone: false
})
export class CustomerInformationCollectionComponent extends BaseViewComponent {

  fb: FormBuilder = inject(FormBuilder)
  #secureDataService: SecureDataService = inject(SecureDataService)
  #signalREventService: SignalREventService = inject(SignalREventService)

  customerInformationForm: FormGroup | undefined;
  filteredAddresses: AddressSuggestion[] = [];
  showFullAddress = false;
  displayStructureType = false;
  displayFloor = false;
  selectedAddress: string | undefined;
  showWaiting = false;
  addresses: AddressSuggestions | undefined;
  showCancelDialog = false;
  isEmailVerifiedByCustomer = false;
  isPhoneVerifiedByCustomer = false;
  addressConfirmationText = '';
  showAddressConfirmPopup = false;
  isSMB = false;
  showTaxId = false;
  businessTypeOptionsSMB = [
    { label: 'Corporation', value: 'corp' },
    { label: 'Limited Liability Company (LLC)', value: 'llc' },
    { label: 'Sole Proprietor', value: 'sole-prop' }
  ];
  private readonly taxIdPattern = /^(?:\d{2}|\*{2})-(?:\d{7}|\*{7}|\*{6}\d{1}|\*{3}\d{4})$/;

  @ViewChild('autoComplete') autoComplete?: AutoComplete;
  @ViewChild('showFullAddressBtn', { static: false }) showFullAddressBtn!: Button;
  @ViewChild('emailVerificationComponent') emailVerificationComponent?: EmailVerificationComponent

  unitTypes = [
    { label: '-', value: null },
    { label: 'Unit', value: 'Unit' },
    { label: 'Suite', value: 'Ste' },
    { label: 'Apartment', value: 'Apt' },
    { label: 'Room', value: 'Room' },
    { label: 'Slip', value: 'Slip' },
    { label: 'Lot', value: 'Lot' }
  ]
  structureTypes = ['Building', 'Pier', 'Wing', 'Trailer']

  onApiEmailValidation($event: EmailVerifyStatus) {
    if ($event === EmailVerifyStatus.VerifiedByCustomer) {
      this.customerInformationForm?.get('isEmailVerifiedByCustomer')?.setValue(true);
    }
  }

  onApiPhoneValidation($event: PhoneVerifyStatus) {
    if ($event === PhoneVerifyStatus.VerifiedByCustomer) {
      this.customerInformationForm?.get('isPhoneVerifiedByCustomer')?.setValue(true);
    }
  }

  protected override refreshView(): void {
    this.customerInformationForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.pattern('[a-zA-Z- \']*')]],
      lastName: ['', [Validators.required, Validators.pattern('[a-zA-Z- \']*')]],
      serviceAddress: ['', [Validators.required, poBoxAddressValidator]],
      city: ['', [Validators.required]],
      state: ['', [Validators.required]],
      zipCode: ['', [Validators.required, Validators.minLength(5)]],
      unitType: ['', []],
      unitNumber: ['', []],
      structureType: ['', []],
      structureNumber: ['', []],
      floor: ['', []],
      email: ['', [Validators.required, Validators.email, Validators.pattern(EMAIL_PATTERN)]],
      phone: ['', [Validators.required, Validators.pattern('[0-9-]{12}')]],
      canContact: [null, [Validators.required]],
      isEmailVerifiedByCustomer: [false],
      isPhoneVerifiedByCustomer: [false],
      businessNameSMB: [''],
      businessTypeSMB: [''],
      businessTaxIdSMB: [''],
      businessTaxIdSMBRaw: ['']
    });

    // When Sole Proprietor is chosen the Tax ID field is hidden and not required.
    this.subscriptions.add(
      this.customerInformationForm.get('businessTypeSMB')?.valueChanges.subscribe((value) => {
        if (this.isSMB) {
          this.updateBusinessTaxIdSMBValidators(value);
          if (value === 'sole-prop') {
            this.customerInformationForm?.get('businessTaxIdSMB')?.setValue('', { emitEvent: false });
            this.customerInformationForm?.get('businessTaxIdSMBRaw')?.setValue('', { emitEvent: false });
          }
        }
      })
    );
  }

  private enableBusinessValidators(): void {
    this.customerInformationForm?.get('businessNameSMB')?.setValidators([Validators.required, Validators.maxLength(200)]);
    this.customerInformationForm?.get('businessTypeSMB')?.setValidators([Validators.required]);
    this.updateBusinessTaxIdSMBValidators(this.customerInformationForm?.get('businessTypeSMB')?.value);
    this.customerInformationForm?.get('businessNameSMB')?.updateValueAndValidity({ emitEvent: false });
    this.customerInformationForm?.get('businessTypeSMB')?.updateValueAndValidity({ emitEvent: false });
  }

  private updateBusinessTaxIdSMBValidators(businessType: string): void {
    const control = this.customerInformationForm?.get('businessTaxIdSMB');
    const validators = [Validators.pattern(this.taxIdPattern)];
    if (businessType !== 'sole-prop') {
      validators.unshift(Validators.required);
    }
    control?.setValidators(validators);
    control?.updateValueAndValidity({ emitEvent: false });
  }

  onTaxIdInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    if (value.length > 2) {
      value = `${value.slice(0, 2)}-${value.slice(2, 9)}`;
    }
    this.customerInformationForm?.get('businessTaxIdSMB')?.setValue(value, { emitEvent: false });
    this.customerInformationForm?.get('businessTaxIdSMBRaw')?.setValue(value.replace(/-/g, ''), { emitEvent: false });
  }

  get formControls() {
    return this.customerInformationForm?.controls;
  }

  formControlByName(name: string) {
    return this.formControls ? this.formControls[name] : undefined;
  }

  protected override onSecureDataChanged(val: StateChangedModel): void {
    if (val.Type == 'CustomerInfo' && val.Data != undefined) {
      const rawData = val.Data;
      if (rawData && typeof rawData == 'object') {
        if ((rawData as { orderType?: string })['orderType'] === 'smbnew') {
          this.isSMB = true;
          this.enableBusinessValidators();
        }
        this.customerInformationForm?.patchValue(val.Data);
        const data = this.customerInformationForm?.value as CustomerInfoResponse;
        if (data?.canContact) {
          const canContact = data?.canContact === 'true' ? true : false;
          this.customerInformationForm?.controls['canContact'].setValue(canContact);
        }
        //if full address infornation coming expand it by default
        this.prefillFullAddress(data);
        this.customerInformationForm?.updateValueAndValidity();

        if (val.Data && typeof val.Data == 'object') {
          if (val.Data['isEmailVerifiedByCustomer'] === true) {
            this.isEmailVerifiedByCustomer = true;
          }

          if (val.Data['isPhoneVerifiedByCustomer'] === true) {
            this.isPhoneVerifiedByCustomer = true;
          }
        }
      }
    }
  }

  filterAddress(event: AutoCompleteCompleteEvent | null, additionalSearch?: string) {
    const enteredAddress = additionalSearch ?? event?.query;

    if (enteredAddress) {
      this.#secureDataService.getSuggestionAddresses(enteredAddress).subscribe({
        next: (resp) => {
          if (resp && resp.suggestions.length > 0) {
            this.addresses = resp
            this.filteredAddresses = this.addresses.suggestions.map((item) => this.mapAddress(item))
          } else {
            this.addresses = { suggestions: [] }
            this.filteredAddresses = []
          }
          if (additionalSearch) {
            this.triggerAutoComplete(resp)
          }
        },
        error: (err) => {
          console.log(err)
          this.addresses = { suggestions: [] }
          this.filteredAddresses = []
          // this.notificationService.showError("Error in getting address suggestions")
        }
      })
    }
  }

  onAddressSelect(event: { value: { value: Suggestion } }) {
    const value = event?.value?.value;
    // when entries is selected
    if (value.entries > 1) {
      this.filterAddress(null, this.buildAdditionalAddressSearchParam(value))
      return
    }
    this.clearAddressSection()
    // when apartment or suite is selected
    if (value.entries === 1) {
      this.onAdditionalAddressSelect(value)
    }

    this.selectedAddress = `${value.street_line}, ${value.city}, ${value.state} ${value.zipcode}`
    this.customerInformationForm?.patchValue({
      serviceAddress: value.street_line,
      zipCode: value.zipcode,
      city: value.city,
      state: value.state
    })
    this.customerInformationForm?.get('serviceAddress')?.markAsTouched()
    this.validateAddress()
  }

  buildAdditionalAddressSearchParam(value: Suggestion): string {
    return `${value.street_line}&selected=${value.street_line} ${value.secondary} (${value.entries}) ${value.city} ${value.state} ${value.zipcode}`
  }

  clearAddressSection() {
    this.customerInformationForm?.get('unitType')?.setErrors(null)
    this.customerInformationForm?.get('unitNumber')?.setErrors(null)
    this.customerInformationForm?.get('unitType')?.removeValidators(Validators.required)
    this.customerInformationForm?.get('unitNumber')?.removeValidators(Validators.required)
    this.customerInformationForm?.updateValueAndValidity()
    this.customerInformationForm?.patchValue({
      serviceAddress: null,
      city: null,
      state: null,
      zipCode: null,
      unitType: null,
      unitNumber: null,
      structureType: null,
      structureNumber: null,
      floor: null
    })
  }

  onAdditionalAddressSelect(value: Suggestion) {
    this.customerInformationForm?.get('unitType')?.addValidators(Validators.required)
    this.customerInformationForm?.get('unitNumber')?.addValidators(Validators.required)
    this.customerInformationForm?.patchValue({
      unitType: value.secondary.split(' ')[0],
      unitNumber: value.secondary.split(' ')[1],
    })
    if (!this.showFullAddress) {
      this.showFullAddressBtn.el.nativeElement.firstChild.click()
    }
  }

  removeStructure() {
    this.displayStructureType = false
    this.customerInformationForm?.patchValue({
      structureType: null,
      structureNumber: null
    })
    this.removeFloor()
  }

  removeFloor() {
    this.displayFloor = false
    this.customerInformationForm?.patchValue({
      floor: null
    })
  }

  validateAddress() {
    const addressFormValue = this.customerInformationForm?.value
    if (addressFormValue.zipCode) {
      console.log(addressFormValue);
    }
  }

  mapAddress(item: Suggestion) {
    let entries = ""
    let unit = ""
    if (item.entries > 1) {
      entries = ` (${item.entries} more entries)`
      unit = ` ${item.secondary}`
    }
    if (item.entries === 1) {
      unit = ` ${item.secondary}`
    }
    return { label: `${item.street_line}${unit}${entries}, ${item.city}, ${item.state} ${item.zipcode}`, value: item }
  }

  triggerAutoComplete(resp: AddressSuggestions) {
    this.customerInformationForm?.controls['serviceAddress'].setValue((resp.suggestions[0]?.street_line ?? '') + ' ' + (resp.suggestions[0]?.secondary.split(' ')[0] ?? ''))
    setTimeout(() => {
      this.autoComplete?.show()
    }, 200)
  }

  onSubmitClicked() {
    if (this.customerInformationForm?.valid) {
      this.showAddressConfirmPopup = true;
      const formValue = this.customerInformationForm.value;
      const address = `${formValue.serviceAddress} ${formValue.city} ${formValue.state} ${formValue.zipCode}`;
      this.addressConfirmationText = address;
    } else {
      this.customerInformationForm?.markAllAsTouched();
      this.scrollToInvalidControl();
      return;
    }
  }

  submit() {
    if (this.customerInformationForm?.valid) {
      const requestModel = this.customerInformationForm.value;
      requestModel.canContact = requestModel.canContact === true ? 'true' : 'false';
      this.#secureDataService.submitCustomerData(this.customerInformationForm.value, 'CustomerInfo')
        .subscribe({
          next: () => {
            this.navigateWaiting();
          },
          error: (err) => {
            this.notificationService.showError(err);
          }
        });
    } else {
      this.customerInformationForm?.markAllAsTouched();
      this.scrollToInvalidControl();
      return;
    }
  }

  onCancelClick() {
    this.showCancelDialog = true;
  }

  onCancel(data: unknown) {
    this.showCancelDialog = false;
    if (data && (data as { data: { success: boolean } }).data?.success) {
      this.#secureDataService.cancel('CustomerInfo').pipe(take(1)).subscribe(
        {
          next: () => {
            this.navigateExpired();
          },
          error: (err) => this.onCancelError('CustomerInfo', err)
        })
    }
  }

  get states() {
    return this.#secureDataService.States;
  }

  prefillFullAddress(data: CustomerInfoResponse) {
    this.showFullAddress = false;
    this.displayStructureType = false;
    this.displayFloor = false;
    if (data) {
      if (data.unitType) {
        this.showFullAddress = true;
      }

      if (data.structureType) {
        this.displayStructureType = true;
      }

      if (data.floor) {
        this.displayFloor = true;
      }
    }
  }

  onConfirmAddressPopupResult(data: unknown) {
    this.showAddressConfirmPopup = false;
    if (data && (data as { data: { success: boolean } }).data?.success) {
      this.submit();
    }
  }
}

export function poBoxAddressValidator(control: AbstractControl): ValidationErrors | null {
  const raw = control.value;
  const address: string = typeof raw === 'string' ? raw : (raw?.value?.street_line ?? raw?.label ?? '');
  if (!address) return null;
  const formattedAddress = address
    .replace(/\./g, '')
    .replace(/-/g, '')
    .replace(/\s/g, '')
    .toLowerCase();
  return formattedAddress.startsWith('pobox') ? { poBox: true } : null;
}

export interface Suggestion {
  street_line: string
  secondary: string
  city: string
  state: string
  zipcode: string
  entries: number
}

export interface AddressSuggestion {
  label: string;
  value: Suggestion
}

export interface CustomerInfoResponse {
  customerId: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  alternatePhone: string;
  govtApproved: boolean;
  canContact: string;
  serviceAddress: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  unitType: string;
  unitNumber: string;
  structureType: string;
  structureNumber: string;
  floor: string;
  orderType?: string;
  businessNameSMB?: string;
  businessTypeSMB?: string;
  businessTaxIdSMB?: string;
  businessTaxIdSMBRaw?: string;
}
